import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef, // Moved
  Component,
  OnDestroy, // Moved
  OnInit, // Moved
  ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Store } from '@ngrx/store';
// Added take(1) for synchronous state fetch, Subscription
import { map, Observable, Subscription, take, timer } from 'rxjs';

import { toggleHelp } from '../../../+state/app.actions';
import { getHelpState } from '../../../+state/app.selectors';
import { GopherBuddyAppState } from '../../../+state/app.state';
import {
  DeviceManagedState,
  StateStatus,
} from '../../../../background-service-worker/services/extension-store/interfaces/extension-store.interfaces';
import { GBMessages } from '../../../../background-service-worker/services/message-handler/message-handler.interfaces';
import { UserAccessType } from '../../../../background-service-worker/services/user-profile/user-profile.state';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-launcher',
  templateUrl: './launcher.component.html',
  styleUrls: ['./launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
// Added OnInit, OnDestroy
export class LauncherComponent implements AfterViewInit, OnInit, OnDestroy {
  public cacheClearing = false;

  public loadingTimedOut = false; // Added timeout flag

  @ViewChild('helpDrawer') helpDrawer!: MatDrawer;

  public appState$: Observable<GopherBuddyAppState>;

  private helpStateSubscription: Subscription | undefined; // Added for cleanup

  private loadingTimeoutSubscription: Subscription | undefined; // Added for timeout cleanup

  constructor(
    public appState: Store<GopherBuddyAppState>,
    public uiService: UiService,
    private cdr: ChangeDetectorRef // Injected ChangeDetectorRef
  ) {
    this.appState$ = appState.pipe(map((gbAppState: GopherBuddyAppState) => gbAppState));
  }

  ngOnInit(): void {
    // Added OnInit
    // Start the loading timeout timer (3 minutes = 180,000 ms)
    this.loadingTimeoutSubscription = timer(180000).subscribe(() => {
      // Use take(1) to get the current state synchronously and complete
      this.appState.pipe(take(1)).subscribe((currentState: GopherBuddyAppState) => {
        if (!this.isLoaded(currentState)) {
          this.loadingTimedOut = true;
          this.cdr.detectChanges(); //
        }
      });
    });
  }

  ngOnDestroy(): void {
    // Added OnDestroy
    // Clean up subscriptions
    if (this.helpStateSubscription) {
      this.helpStateSubscription.unsubscribe();
    }
    if (this.loadingTimeoutSubscription) {
      this.loadingTimeoutSubscription.unsubscribe();
    }
  }

  public ngAfterViewInit(): void {
    // Store subscription for cleanup
    this.helpStateSubscription = this.appState
      .select(getHelpState)
      .subscribe(async (helpState: boolean): Promise<void> => {
        if (!this.helpDrawer) return; // Guard against potential timing issues
        if (helpState) {
          await this.helpDrawer.open();
          return;
        }
        await this.helpDrawer.close();
      });

    // If the user closes the infoDrawer mark all toggle-able Panels as closed.
    // Ensure helpDrawer exists before subscribing
    if (this.helpDrawer) {
      this.helpDrawer.openedChange.subscribe((isOpen: boolean) => {
        if (!isOpen) {
          this.appState.dispatch(toggleHelp({ open: false }));
        }
      });
    }
  }

  public isCheckedIn = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.checkInState.status === StateStatus.SUCCESS;

  public isLoaded = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.globalAppFlagsState.isLoaded === true;

  public isValidCustomer = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.checkInState.isCustomerValid ?? false;

  public isManaged = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.globalAppFlagsState.deviceManagedState === DeviceManagedState.MANAGED;

  public hasErrors = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.checkInState.status === StateStatus.FAILED;

  public hasDeviceInfoAccess = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.userProfile.userProfile?.accessLevel === UserAccessType.BASIC ||
    appState.app.settings?.userProfile.userProfile?.accessLevel === UserAccessType.FULL;

  public checkVersionEnabled = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.customerState.customerSettings?.settings.allowChromeOsUpdateReminders ?? false;

  public versionIsCurrent = (appState: GopherBuddyAppState): boolean =>
    appState.app.settings?.versionUpdateState.updateNeeded === false;

  public lastVersionCheck = (appState: GopherBuddyAppState): Date =>
    new Date(appState.app.settings?.checkInState.lastCheckIn ?? Date.now());

  public clearCache = (): void => {
    this.cacheClearing = true;
    chrome.runtime.sendMessage(GBMessages.CLEAR_CACHE, (response: { success: boolean }) => {
      this.cacheClearing = false;
      const message = response.success
        ? 'Your device cache has successfully been cleared.'
        : 'Your device cache failed to clear';
      this.uiService.showNotification('clear_cache', message);
      this.cdr.detectChanges(); // Trigger change detection after async operation
    });
  };

  public openDeviceDetails = (): void => {
    chrome.runtime.sendMessage(GBMessages.OPEN_DEVICE_INFO, () => {});
  };

  public openUpdateHelp = (): void => {
    chrome.runtime.sendMessage(GBMessages.OPEN_HELP, () => {});
  };

  public onFixVersion = (): void => {
    chrome.runtime.sendMessage(GBMessages.FIX_VERSION, () => {});
  };
}
